import React, { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { Button } from '../atoms/button';
import { Input } from '../atoms/input';
import { Popover, PopoverContent, PopoverTrigger } from '../atoms/popover';
import { INDUSTRIES, type IndustryOption } from '../../lib/industries';
import { cn } from '../../lib/utils';

export interface IndustryMultiSelectProps {
  /** Comma-separated string of selected option values. */
  value: string;
  /** Receives the updated comma-separated string. */
  onChange: (value: string) => void;
  /** Defaults to the shared {@link INDUSTRIES} catalog. */
  options?: IndustryOption[];
  className?: string;
}

/**
 * Multi-select industry picker used by onboarding and the startup profile so the
 * two stay identical. `value` is a comma-separated string of selected option
 * values; the popover stays open on select so users can pick several.
 */
export function IndustryMultiSelect({
  value,
  onChange,
  options = INDUSTRIES,
  className,
}: IndustryMultiSelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedValues = value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
  const selectedSet = new Set(selectedValues);
  const selectedLabels = selectedValues.map(
    (v) => options.find((o) => o.value === v)?.label ?? v
  );

  const toggle = (optValue: string) => {
    const next = new Set(selectedSet);
    if (next.has(optValue)) next.delete(optValue);
    else next.add(optValue);
    // Preserve the option list's order rather than click order.
    onChange(
      options
        .filter((o) => next.has(o.value))
        .map((o) => o.value)
        .join(',')
    );
  };

  const filtered = query.trim()
    ? options.filter((opt) => {
        const q = query.trim().toLowerCase();
        return opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q);
      })
    : options;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary-gray"
          className={cn('w-full justify-between font-normal h-10', className)}
        >
          <span
            className={cn(
              'truncate',
              selectedLabels.length ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {selectedLabels.length ? selectedLabels.join(', ') : 'Select industries'}
          </span>
          <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <div className="p-2 border-b border-border">
          <Input
            autoFocus
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            placeholder="Search industries…"
          />
        </div>
        {selectedSet.size > 0 && (
          <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
            <span className="text-xs text-muted-foreground">{selectedSet.size} selected</span>
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-xs font-medium text-blue-600 transition-colors hover:text-blue-700"
            >
              Clear
            </button>
          </div>
        )}
        <div className="max-h-64 overflow-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-sm text-muted-foreground text-center">No matches</div>
          ) : (
            filtered.map((opt) => {
              const isSelected = selectedSet.has(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggle(opt.value)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted',
                    isSelected && 'bg-muted/60 font-medium'
                  )}
                >
                  <span className="truncate">{opt.label}</span>
                  {isSelected && <Check className="w-4 h-4 shrink-0 text-blue-600" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default IndustryMultiSelect;
